import { useRef, useEffect } from 'react';
import SplitText from '../components/SplitText';

const PRODUCTS = [
  {
    id: 1,
    title: 'Automotive Graphics',
    image: '/Images/i6 vehicle.png',
    desc: 'At Decal Tech, we craft automotive graphics that not only enhance a vehicle\'s appearance but also provide long-lasting performance. Whether it\'s for fleet branding, commercial vehicles, or custom designs, our graphics are engineered to endure the elements, ensuring your vehicle stands out on the road with vibrant, durable visuals.',
    tag: 'Automotive',
  },
  {
    id: 2,
    title: 'Helmet Decals',
    image: '/Images/product.png',
    desc: 'Our helmet decals are designed to add a unique touch to your helmet. Primarily used for aesthetic purposes, they offer a variety of custom designs that help make your helmet look stylish and distinctive. Built to last, our decals maintain their vibrancy and durability, giving your helmet a personalized flair.',
    tag: 'Water Slide',
  },
  {
    id: 3,
    title: 'Self-Adhesive Labels',
    image: '/Images/product4.jpeg',
    desc: 'Decal Tech offers a wide range of self-adhesive labels tailored to meet the demands of various industries. These labels are perfect for packaging, product identification, and logistics, adhering securely to different surfaces while maintaining sharp, clear graphics. Our commitment to quality ensures that your labels perform well in any environment.',
    tag: 'Multi-Substrate',
  },
  {
    id: 5,
    title: 'Doming Labels',
    image: '/Images/product3.png',
    desc: 'Doming labels from Decal Tech add a polished, three-dimensional effect to your products. Featuring a glossy resin coating, these labels are resistant to scratches, water, and UV rays, making them an ideal choice for applications in electronics, automotive, and branded products. They offer a sophisticated look with long-lasting durability.',
    tag: 'OEM Marking',
  },
  {
    id: 6,
    title: 'Vehicle Wraps',
    image: '/Images/product5.jpeg',
    desc: 'Decal Tech\'s vehicle wraps offer a sleek and stylish solution with a single, bold black colour. Designed for personal customization, our wraps are perfect for adding a sophisticated touch to vehicles. Made to withstand the elements, these wraps are durable and ensure your vehicle maintains a clean, professional look for years to come.',
    tag: 'Cut to Size & Shape',
  },
  {
    id: 7,
    title: 'Membrane Switches & Graphic Overlays',
    image: '/Images/product7.jpeg',
    desc: 'Our membrane switches and graphic overlays combine functionality with visual appeal. Perfect for industrial and electronic applications, these overlays are designed to be both durable and easy to operate, providing a seamless user interface. Each product is built to withstand frequent use, ensuring longevity and reliability.',
    tag: 'Industrial & Electronic',
  },
];

const useScrollAnimation = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
};

const animationStyles = `
  .products-page,
  .products-page * {
    font-family: 'Inter', sans-serif !important;
  }

  @keyframes fadeSlideLeft {
    from { opacity: 0; transform: translateX(-48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeSlideRight {
    from { opacity: 0; transform: translateX(48px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(36px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .product-row-animate { opacity: 0; }
  .product-row-animate.animate-in { opacity: 1; }

  .product-row-animate.animate-in .img-left {
    animation: fadeSlideLeft 0.65s cubic-bezier(0.22,1,0.36,1) forwards;
  }
  .product-row-animate.animate-in .img-right {
    animation: fadeSlideRight 0.65s cubic-bezier(0.22,1,0.36,1) forwards;
  }
  .product-row-animate.animate-in .text-right {
    animation: fadeSlideRight 0.65s cubic-bezier(0.22,1,0.36,1) 0.12s forwards;
  }
  .product-row-animate.animate-in .text-left {
    animation: fadeSlideLeft 0.65s cubic-bezier(0.22,1,0.36,1) 0.12s forwards;
  }

  .hero-animate {
    opacity: 0;
    animation: fadeSlideUp 0.7s cubic-bezier(0.22,1,0.36,1) 0.1s forwards;
  }
`;

/* Square image box — fixed 420×420, objectFit cover = cropped square */
const ProductImage = ({ src, alt }) => (
  <div style={{
    width: '390px',
    height: '390px',
    flexShrink: 0,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#f7f7f7',
  }}>
    {src ? (
      <img
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
    ) : (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ccc',
        fontSize: '13px',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      }}>
        Image Coming Soon
      </div>
    )}
  </div>
);

const ProductRow = ({ product, index }) => {
  const isEven = index % 2 === 0;
  const rowRef = useScrollAnimation();

  return (
    <div
      ref={rowRef}
      className="product-row-animate"
      style={{
        borderBottom: '1px solid #efefef',
        padding: '64px 0',
      }}
    >
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: isEven ? 'row' : 'row-reverse',
        alignItems: 'center',
        gap: '72px',
      }}>

        {/* Image */}
        <div
          className={isEven ? 'img-left' : 'img-right'}
          style={{ opacity: 0, flexShrink: 0 }}
        >
          <ProductImage src={product.image} alt={product.title} />
        </div>

        {/* Text — aligned to its own left edge always */}
        <div
          className={isEven ? 'text-right' : 'text-left'}
          style={{ opacity: 0, flex: 1, minWidth: 0 }}
        >
          {/* Tag */}
          <span style={{
            display: 'block',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#C8102E',
            marginBottom: '10px',
          }}>
            {product.tag}
          </span>

          {/* Title */}
          <h2 style={{
            fontSize: 'clamp(1.6rem, 2.4vw, 2rem)',
            fontWeight: 800,
            color: '#111111',
            letterSpacing: '-0.4px',
            lineHeight: 1.2,
            margin: '0 0 14px 0',
          }}>
            {product.title}
          </h2>

          {/* Red line — starts at left edge of text block, fixed width */}
          <div style={{
            height: '3px',
            width: '40px',
            background: '#C8102E',
            borderRadius: '2px',
            marginBottom: '20px',
            /* no margin-left — aligns naturally to text start */
          }} />

          {/* Description */}
          <p style={{
            fontSize: '15px',
            color: '#555555',
            lineHeight: '1.82',
            margin: 0,
          }}>
            {product.desc}
          </p>
        </div>

      </div>
    </div>
  );
};

const OurProductscreen = () => {
  return (
    <div
      className="products-page"
      style={{
      backgroundColor: '#fcfbfa',
      minHeight: '100vh',
      overflowX: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{animationStyles}</style>

      {/* Hero */}
      <div
        className="hero-animate"
        style={{
          textAlign: 'center',
          padding: '80px 24px 48px',
        }}
      >
        <h1 style={{
          fontSize: 'clamp(2rem, 4vw, 2.8rem)',
          fontWeight: 800,
          color: '#111',
          margin: '0 0 14px 0',
          letterSpacing: '-0.5px',
        }}>
          Our Products
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#666666',
          maxWidth: '520px',
          margin: '0 auto',
          lineHeight: 1.75,
        }}>
          Premium labels, graphics, and decals engineered for durability — across automotive, industrial, and consumer applications.
        </p>
      </div>

      {/* Product Rows */}
      <div style={{ padding: '0 5% 100px' }}>
        {PRODUCTS.map((product, i) => (
          <ProductRow key={product.id} product={product} index={i} />
        ))}
      </div>
    </div>
  );
};

export default OurProductscreen;